import axios from 'axios';

// IMPORTANTE: No establecer baseURL aquí, se establece dinámicamente en el interceptor
// para que se evalúe en runtime y use el protocolo correcto (HTTPS)
const api = axios.create({
  // ✅ NO establecer Content-Type por defecto - dejar que axios lo maneje automáticamente
  // Esto permite que multipart/form-data se establezca correctamente para uploads
  maxRedirects: 5, // Follow redirects
});

// Add token to requests
api.interceptors.request.use(
  (config) => {
    // 🔒 FORZAR HTTPS: Construir URL completa dinámicamente
    // Si la URL no es absoluta, construir con el protocolo de la página
    if (config.url && !config.url.startsWith('http')) {
      const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      if (isLocalhost) {
        // El proxy de CRA (puerto 3002) descartaba Authorization en PUT y
        // DELETE. En local conectamos directo al backend, que ya permite CORS
        // para este origen, para conservar la sesión en toda solicitud.
        const localBackendUrl = (process.env.REACT_APP_BACKEND_URL || 'http://127.0.0.1:8002').replace(/\/$/, '');
        config.url = `${localBackendUrl}/api${config.url}`;
      } else {
        // En producción, forzar HTTPS explícitamente
        config.url = `https://${window.location.host}/api${config.url}`;
      }
    }
    
    // ✅ Establecer Content-Type solo si no es FormData
    // FormData necesita que axios establezca el boundary automáticamente
    if (!(config.data instanceof FormData) && !config.headers['Content-Type']) {
      config.headers['Content-Type'] = 'application/json';
    }
    
    // 🔍 Log para debugging de media
    if (config.url?.includes('/messages') && config.method === 'post') {
      console.log('🌐 [API REQUEST] Enviando mensaje:', {
        url: config.url,
        method: config.method,
        data: config.data
      });
    }
    
    // 📱 MULTI-NÚMERO: Agregar header X-WhatsApp-Connection-Id si está configurado
    const activeConnectionId = localStorage.getItem('activeChannelConnectionId')
      || localStorage.getItem('activeWhatsAppConnectionId');
    const activeChannelType = localStorage.getItem('activeChannelType') || 'whatsapp';
    // Axios v1 guarda los headers en AxiosHeaders. Consultarlo como objeto
    // plano devolvía undefined y podía reemplazar la conexión explícita.
    const existingConnectionId = typeof config.headers?.get === 'function'
      ? config.headers.get('X-WhatsApp-Connection-Id')
      : config.headers?.['X-WhatsApp-Connection-Id'];
    if (activeConnectionId && activeChannelType === 'whatsapp' && !existingConnectionId) {
      if (typeof config.headers?.set === 'function') {
        config.headers.set('X-WhatsApp-Connection-Id', activeConnectionId);
      } else {
        config.headers['X-WhatsApp-Connection-Id'] = activeConnectionId;
      }
    }
    if (activeConnectionId) {
      if (typeof config.headers?.set === 'function') {
        config.headers.set('X-Channel-Connection-Id', activeConnectionId);
      } else {
        config.headers['X-Channel-Connection-Id'] = activeConnectionId;
      }
    }
    
    // Axios 1 usa AxiosHeaders. Leer/asignar como una propiedad simple puede
    // dejar el header fuera de la solicitud al pasar por el proxy local.
    const existingAuthorization = typeof config.headers?.get === 'function'
      ? config.headers.get('Authorization')
      : config.headers?.Authorization;

    // Si ya tiene Authorization header (ej: marketplace con token explícito), no sobrescribir
    if (!existingAuthorization) {
      // CRITICAL: Priorizar adminToken si existe (portal admin tiene prioridad)
      const adminToken = localStorage.getItem('adminToken');
      const clientToken = localStorage.getItem('token');
      
      // Si estamos en ruta admin O existe adminToken, usar adminToken
      const isAdminPortal = window.location.pathname.startsWith('/admin');
      
      let token = null;
      if (isAdminPortal && adminToken) {
        token = adminToken;
      } else if (adminToken && !clientToken) {
        // Si solo existe adminToken, usarlo (usuario admin navegando)
        token = adminToken;
      } else {
        // Portal cliente o ambos tokens existen, usar token de cliente
        token = clientToken;
      }
      
      if (token) {
        const authorization = `Bearer ${token}`;
        if (typeof config.headers?.set === 'function') {
          config.headers.set('Authorization', authorization);
        } else {
          config.headers.Authorization = authorization;
        }
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle responses
api.interceptors.response.use(
  (response) => {
    // 🔍 Log para debugging de media
    if (response.config?.url?.includes('/messages') && response.config?.method === 'post') {
      console.log('✅ [API RESPONSE] Respuesta del servidor:', {
        status: response.status,
        statusText: response.statusText,
        data: response.data
      });
    }
    return response;
  },
  (error) => {
    // 🔍 Log de errores en envío de media
    if (error.config?.url?.includes('/messages')) {
      console.error('❌ [API ERROR] Error en envío de mensaje:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message
      });
    }
    
    // Las pantallas de Farmacias ya manejan sus propios errores de carga. No se
    // debe cerrar la sesión completa por un 401 aislado del catálogo: además de
    // expulsar al usuario, impedía ver la causa concreta y reintentar.
    const isPharmacyRequest = error.config?.url?.includes('/pharmacy/');
    // Gestionar etiquetas no debe invalidar la sesión por una falla aislada de
    // la ruta. El formulario muestra el error y permite reintentar.
    const isTagsRequest = error.config?.url?.includes('/tags/');

    if (error.response?.status === 401 && !isPharmacyRequest && !isTagsRequest) {
      // No redirigir si estamos en el marketplace público
      const isPublicMarketplace = window.location.pathname.startsWith('/marketplace');
      const isAdminPortal = window.location.pathname.startsWith('/admin');
      
      if (!isPublicMarketplace) {
        if (isAdminPortal) {
          // Portal Admin - limpiar tokens admin y redirigir a login admin
          localStorage.removeItem('adminToken');
          localStorage.removeItem('adminUser');
          window.location.href = '/admin/login';
        } else {
          // Portal Cliente - limpiar tokens cliente y redirigir a login cliente
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          window.location.href = '/login';
        }
      }
    }

    if (error.response?.status === 401 && (isPharmacyRequest || isTagsRequest)) {
      console.error('❌ [SOLICITUD AISLADA] La solicitud fue rechazada sin cerrar la sesión:', {
        url: error.config?.url,
        detail: error.response?.data?.detail,
      });
    }
    return Promise.reject(error);
  }
);

export default api;
